import { useCallback, useMemo } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import type { UnifiedWallet } from '../auth/useWallet';
import { useSubaccounts } from '../hooks/useSubaccounts';
import { useLegacyWallet } from '../hooks/useLegacyWallet';
import { usePrices } from '../hooks/usePrices';
import { SubaccountCard } from './SubaccountCard';
import { LegacyWalletCard } from './LegacyWalletCard';
import {
  LAMPORTS_PER_SOL,
  ORE_DECIMALS,
  formatOre,
  formatSol,
  formatUsd,
  shortenPubkey,
} from '../lib/evore';

export function Dashboard({ wallet }: { wallet: UnifiedWallet }) {
  const { t } = useTranslation();
  const { loading, error, subaccounts, lastUpdatedAt, refresh } = useSubaccounts(wallet.publicKey);
  const legacy = useLegacyWallet(wallet.publicKey);
  const pricesState = usePrices();
  const { solUsd, oreUsd } = pricesState.prices;

  // Refresh button refetches subaccount + legacy + prices in one click.
  const refreshAll = useCallback(async () => {
    await Promise.all([refresh(), legacy.refresh(), pricesState.refresh()]);
  }, [refresh, legacy.refresh, pricesState.refresh]);

  // Render the legacy card whenever the wallet has either an OreMiner PDA or
  // a non-empty ORE token account — otherwise non-legacy users see nothing.
  const showLegacy =
    legacy.balances != null &&
    (legacy.balances.hasOreMiner || legacy.balances.ataOre > 0n);

  const totals = useMemo(() => {
    let sol = 0n;
    let unrefined = 0n;
    let refined = 0n;
    for (const s of subaccounts) {
      sol += s.mmaLamports;
      unrefined += s.rewardsOre;
      refined += s.liveRefinedOre;
    }
    // Fold in legacy claimable balances when the legacy card is showing. We
    // include only the claimable side (rewardsSol / rewardsOre / liveRefinedOre)
    // and intentionally exclude `ataOre` to match the subaccount totals
    // semantic of "assets still locked", not "everything the wallet owns".
    if (showLegacy && legacy.balances) {
      sol += legacy.balances.rewardsSol;
      unrefined += legacy.balances.rewardsOre;
      refined += legacy.balances.liveRefinedOre;
    }
    return { sol, unrefined, refined };
  }, [subaccounts, showLegacy, legacy.balances]);

  // USD value of all SOL + ORE in totals. Null if either price is unavailable
  // so the UI can render an em-dash instead of misleading partial number.
  const totalValueUsd = useMemo(() => {
    if (solUsd == null || oreUsd == null) return null;
    const solValue = (Number(totals.sol) / Number(LAMPORTS_PER_SOL)) * solUsd;
    const oreAmount =
      Number(totals.unrefined + totals.refined) / 10 ** ORE_DECIMALS;
    return solValue + oreAmount * oreUsd;
  }, [totals, solUsd, oreUsd]);

  return (
    <div className="win98-window">
      <div className="win98-title-bar">
        <div className="win98-title">
          <i className="fa-solid fa-folder-open" /> {t('sunset.dashboard.title')}
        </div>
      </div>

      <div className="win98-content">
        <div
          className="win98-header-row"
          style={{ gap: 8, justifyContent: 'flex-start', alignItems: 'center' }}
        >
          <button
            className="win98-button"
            onClick={() => void refreshAll()}
            disabled={loading || legacy.loading || pricesState.loading}
            title={t('sunset.dashboard.refreshTitle')}
          >
            <i
              className={`fa-solid fa-sync ${
                loading || legacy.loading || pricesState.loading ? 'fa-spin' : ''
              }`}
            />
            <span style={{ marginLeft: 6 }}>
              {loading || legacy.loading || pricesState.loading
                ? t('sunset.dashboard.refreshing')
                : t('sunset.dashboard.refresh')}
            </span>
          </button>
          {lastUpdatedAt && (
            <span
              className="win98-text-normal"
              style={{ fontSize: 11, color: 'var(--win98-text-muted)' }}
            >
              {t('sunset.dashboard.lastUpdated', {
                time: lastUpdatedAt.toLocaleTimeString(),
              })}
            </span>
          )}
        </div>

        <div
          className="win98-header-row win98-mb-xs"
          style={{ alignItems: 'center', gap: 8, justifyContent: 'space-between' }}
        >
          <span className="win98-section-heading" style={{ margin: 0 }}>
            {t('sunset.dashboard.totals')}
          </span>
          <span
            className="win98-text-normal"
            style={{ fontSize: 11, color: 'var(--win98-text-muted)' }}
          >
            {t('sunset.dashboard.orePrice', {
              price: oreUsd != null ? formatUsd(oreUsd, 4) : '—',
            })}
          </span>
        </div>
        <div className="win98-sunken portfolio-sunken-group win98-mb-md">
          <div className="portfolio-stat-grid">
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">
                {t('sunset.dashboard.statSubaccounts')}
              </span>
              <span className="portfolio-stat-value">{subaccounts.length}</span>
            </div>
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">
                {t('sunset.dashboard.statTotalSol')}
              </span>
              <span className="portfolio-stat-value">
                {formatSol(totals.sol, 6)}
              </span>
            </div>
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">
                {t('sunset.dashboard.statUnrefinedOre')}
              </span>
              <span className="portfolio-stat-value win98-text-ore">
                {formatOre(totals.unrefined, 6)}
              </span>
            </div>
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">
                {t('sunset.dashboard.statRefinedOre')}
              </span>
              <span className="portfolio-stat-value win98-text-ore">
                {formatOre(totals.refined, 6)}
              </span>
            </div>
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">
                {t('sunset.dashboard.statTotalValue')}
              </span>
              <span className="portfolio-stat-value">
                {totalValueUsd != null ? formatUsd(totalValueUsd) : '—'}
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="win98-modal-error win98-mb-md">
            <i className="fa-solid fa-triangle-exclamation" /> {t(error)}
          </div>
        )}

        {showLegacy && legacy.balances && (
          <div style={{ marginBottom: 12 }}>
            <LegacyWalletCard
              wallet={wallet}
              balances={legacy.balances}
              onAfterAction={() => void refreshAll()}
            />
          </div>
        )}

        {!loading && !error && subaccounts.length === 0 && !showLegacy && (
          <div className="win98-empty-state">
            <i className="fa-solid fa-folder-open win98-empty-icon" />
            <h3 className="win98-empty-title">
              {t('sunset.dashboard.emptyTitle')}
            </h3>
            <p className="win98-empty-text">
              {t('sunset.dashboard.emptyText', {
                address: wallet.publicKey ? shortenPubkey(wallet.publicKey, 4) : '',
              })}
            </p>
          </div>
        )}

        {subaccounts.length > 0 && (
          <div className="win98-subaccount-list">
            {subaccounts.map((s, i) => (
              <div key={s.manager.toBase58()} style={{ marginBottom: 12 }}>
                <div className="win98-section-header">
                  {t('sunset.dashboard.subaccountLabel', {
                    n: i + 1,
                    address: shortenPubkey(s.manager, 6),
                  })}
                </div>
                <SubaccountCard
                  wallet={wallet}
                  subaccount={s}
                  onAfterAction={() => void refreshAll()}
                />
              </div>
            ))}
          </div>
        )}

        <p
          className="win98-text-normal"
          style={{ fontSize: 12, marginTop: 16 }}
        >
          {t('sunset.dashboard.explainer')}
        </p>
      </div>
    </div>
  );
}
